import React, { FC, useState } from 'react';
import { ReactComponent as OrganisationIcon } from '../../icons/organisation-users.svg';
import { ReactComponent as UserPlusIcon } from '../../icons/user-plus.svg';
import OrganisationModal from './OrganisationModal';
import OrganisationToggle from './OrganisationToggle';

export const ORGANISATION_NAME_REQUIRED =
  'You need to add a name for your organisation';

const ADD_MY_ASSISTANT_LABEL = 'Add my secretary to this organisation';

interface CreateOrganisationModalProps {
  /** The caller has a PA that is free to be seated in the new organisation */
  canAddMyAssistant: boolean;
  submitting: boolean;
  /** Resolves to an error message, or null once the organisation exists */
  onCreate: (
    name: string,
    includeMyAssistant: boolean,
  ) => Promise<string | null>;
  onClose: () => void;
}

const CreateOrganisationModal: FC<CreateOrganisationModalProps> = ({
  canAddMyAssistant,
  submitting,
  onCreate,
  onClose,
}) => {
  const [name, setName] = useState('');
  const [addMyAssistant, setAddMyAssistant] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [missingName, setMissingName] = useState(false);
  const includeMyAssistant = canAddMyAssistant && addMyAssistant;

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setMissingName(true);
      setError(ORGANISATION_NAME_REQUIRED);
      return;
    }
    setError(await onCreate(trimmed, includeMyAssistant));
  };

  return (
    <OrganisationModal
      icon={<OrganisationIcon />}
      title='Create organisation'
      error={error}
      busy={submitting}
      testId='create-organisation-modal'
      onClose={onClose}
      onSubmit={submit}
      actions={
        <>
          <button
            type='button'
            className='organisation-modal-cancel'
            disabled={submitting}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type='submit'
            className='organisation-modal-primary'
            disabled={submitting}
          >
            {submitting ? 'Creating…' : 'Create organisation'}
          </button>
        </>
      }
    >
      <div className='organisation-modal-panel'>
        <label className='organisation-modal-field inline'>
          <span className='organisation-modal-label'>Organisation name</span>
          <input
            type='text'
            className='organisation-modal-input'
            placeholder='Eg. Acme Inc.'
            value={name}
            maxLength={120}
            autoFocus
            disabled={submitting}
            aria-invalid={missingName && !name.trim()}
            onChange={(e) => {
              setName(e.target.value);
              if (missingName && e.target.value.trim()) {
                setMissingName(false);
                setError(null);
              }
            }}
          />
        </label>
        {canAddMyAssistant && (
          <div className='organisation-modal-switch'>
            <span className='organisation-modal-label' aria-hidden='true'>
              {ADD_MY_ASSISTANT_LABEL}
            </span>
            <OrganisationToggle
              on={addMyAssistant}
              disabled={submitting}
              label={ADD_MY_ASSISTANT_LABEL}
              onChange={setAddMyAssistant}
            />
          </div>
        )}
      </div>
      {includeMyAssistant && (
        <div className='organisation-modal-notice'>
          <UserPlusIcon aria-hidden='true' />
          <div>
            <strong>
              Your secretary will be permanently associated with this new
              organisation
            </strong>
            <span>
              If you&rsquo;re happy with this, choose &lsquo;Create
              organisation&rsquo;
            </span>
          </div>
        </div>
      )}
    </OrganisationModal>
  );
};

export default CreateOrganisationModal;
